import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Déposez les photos dans client/public/photos/ sous ces noms exacts.
// Celles qui manquent sont retirées à la volée, la galerie disparaît si aucune ne charge.
const PHOTOS = [
  '01', '02', '03', '04', '05', '06',
  '07', '08', '09', '10', '11',
].map((n) => `/photos/${n}.jpg`);

const CEREMONY = new Date('2026-08-15T00:00:00+02:00');

const VENUE = {
  name: 'Domaine de Valbonne',
  street: '2094 Chem. de Gavanon',
  city: '30130 Saint-Paulet-de-Caisson',
};

const MAPS_URL =
  'https://www.google.com/maps/search/?api=1&query=' +
  encodeURIComponent(`${VENUE.name}, ${VENUE.street}, ${VENUE.city}`);

type Moment = {
  time: string;
  title: string;
  note: string;
  vip?: boolean;
};

type Day = {
  key: 'fri' | 'sat' | 'sun';
  label: string;
  date: string;
  moments: Moment[];
};

// Le vendredi (welcome dinner) et le goodbye dinner du dimanche soir ne sont
// servis qu'aux invités VIP ; le reste du programme est commun aux deux pages.
const FRIDAY: Day = {
  key: 'fri',
  label: 'Vendredi',
  date: '14 août',
  moments: [
    {
      time: '19h00',
      title: 'Welcome dinner',
      note: 'Soirée pizzas en toute convivialité pour inaugurer le week-end.',
      vip: true,
    },
  ],
};

const SATURDAY: Day = {
  key: 'sat',
  label: 'Samedi',
  date: '15 août',
  moments: [
    {
      time: '16h00',
      title: 'La cérémonie laïque',
      note: 'Arrivez quelques minutes en avance : un rafraîchissement vous attendra le temps de vous installer.',
    },
    {
      time: '17h30',
      title: 'Le cocktail',
      note: 'À l’ombre des arbres : des bulles, de quoi grignoter largement, et quelques surprises qu’on vous laisse découvrir.',
    },
    {
      time: '20h00',
      title: 'Le dîner',
      note: 'Une belle table, une bonne cuisine, et le plaisir d’être enfin tous réunis.',
    },
    {
      time: 'puis',
      title: 'La soirée',
      note: 'La piste est à vous, aussi tard que vous le voudrez.',
    },
  ],
};

const SUNDAY_BASE: Moment[] = [
  {
    time: '11h00',
    title: 'Le brunch',
    note: 'On se retrouve en douceur jusqu’à 14h. Venez à votre rythme.',
  },
  {
    time: '14h00',
    title: 'Piscine et farniente',
    note: 'On prolonge au bord de l’eau jusqu’à 18h30.',
  },
];

const GOODBYE_DINNER: Moment = {
  time: '19h00',
  title: 'Goodbye dinner',
  note: 'Buffet à partager en petit comité pour profiter d’une dernière soirée ensemble.',
  vip: true,
};

const buildProgram = (vip: boolean): Day[] => {
  const sunday: Day = {
    key: 'sun',
    label: 'Dimanche',
    date: '16 août',
    moments: vip ? [...SUNDAY_BASE, GOODBYE_DINNER] : SUNDAY_BASE,
  };
  return vip ? [FRIDAY, SATURDAY, sunday] : [SATURDAY, sunday];
};

const daysUntil = (target: Date): number =>
  Math.ceil((target.getTime() - Date.now()) / 86400000);

type LandingProps = {
  vip?: boolean;
};

const Landing: React.FC<LandingProps> = ({ vip = false }) => {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLElement>(null);
  const days = daysUntil(CEREMONY);
  const [photos, setPhotos] = useState<string[]>(PHOTOS);
  const program = buildProgram(vip);

  // Bande photo : les flèches s'effacent une fois arrivé au bout.
  const galleryRef = useRef<HTMLElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const updateEdges = () => {
    const el = galleryRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  };

  const scrollGallery = (direction: 1 | -1) => {
    const el = galleryRef.current;
    if (!el) return;
    const first = el.querySelector('img');
    const step = first ? first.clientWidth + 2 : 320;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  // Révélation au défilement. La classe n'est posée que si le JS tourne,
  // pour que le contenu reste visible sinon.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (reduced || !('IntersectionObserver' in window)) return;

    root.classList.add('wed--reveal');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px' }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  return (
    <main className="wed" ref={rootRef}>
      <header className="wed-hero">
        <div className="wed-hero-panel">
          <p className="wed-kicker">
            <span>15.08.2026</span>
            <span className="wed-kicker-sep" aria-hidden="true" />
            <span>Saint-Paulet-de-Caisson</span>
            {vip && <span className="wed-vip-pill">Invité VIP</span>}
          </p>

          <h1 className="wed-names">
            <span className="wed-names-line">Estelle</span>
            <span className="wed-amp" aria-hidden="true">&amp;</span>
            <span className="wed-names-line">Rémi</span>
          </h1>

          <p className="wed-hero-sub">
            se disent oui, et rien ne serait pareil sans vous.
          </p>

          <div className="wed-hero-foot">
            {days > 0 ? (
              <p className="wed-count">
                <span className="wed-count-num">{days}</span>
                <span className="wed-count-unit">
                  {days > 1 ? 'jours' : 'jour'} avant le grand saut
                </span>
              </p>
            ) : (
              <p className="wed-count">
                <span className="wed-count-unit">Le grand jour est arrivé</span>
              </p>
            )}
            <a className="wed-hero-link" href="#programme">
              <span className="wed-hero-link-label">Voir le programme</span>
              <svg
                className="wed-arrow"
                viewBox="0 0 14 26"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M7 1 V23 M1.5 17.5 L7 23.5 L12.5 17.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>

        <div className="wed-hero-media">
          <img src="/photos/hero.jpg" alt="Estelle et Rémi" />
        </div>
      </header>

      <nav className="wed-bar" aria-label="Navigation du site">
        <span className="wed-bar-name">
          Estelle &amp; Rémi
          {vip && <span className="wed-bar-vip">VIP</span>}
        </span>
        <span className="wed-bar-links">
          <a href="#programme">Programme</a>
          <a href="#tenue">Tenue</a>
          <a href="#lieu">Lieu</a>
        </span>
        <button
          type="button"
          className="wed-bar-cta"
          onClick={() => navigate('/rsvp')}
        >
          Répondre
        </button>
      </nav>

      <section className="wed-block" id="programme" aria-labelledby="programme-t">
        <div className="wed-block-label">
          <h2 className="wed-label" id="programme-t">Le programme</h2>
        </div>

        <div className="wed-block-body">
          <p className="wed-lede" data-reveal>
            {vip
              ? 'Trois jours entiers, du vendredi soir au dimanche soir. Venez, restez, prenez tout votre temps.'
              : 'Un week-end entier, du samedi après-midi au dimanche en fin de journée. Venez, restez, prenez votre temps.'}
          </p>

          {program.map((day) => (
            <div className={`wed-day wed-day--${day.key}`} key={day.key} data-reveal>
              <h3 className="wed-day-head">
                <span className="wed-day-name">{day.label}</span>
                <span className="wed-day-date">{day.date}</span>
              </h3>

              <ol className="wed-events">
                {day.moments.map((moment) => (
                  <li className="wed-event" key={moment.title}>
                    <span className="wed-event-time">{moment.time}</span>
                    <div>
                      <h4 className="wed-event-title">
                        {moment.title}
                        {moment.vip && (
                          <span className="wed-event-tag">VIP</span>
                        )}
                      </h4>
                      <p className="wed-event-note">{moment.note}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>

      <section className="wed-block" id="tenue" aria-labelledby="tenue-t">
        <div className="wed-block-label">
          <h2 className="wed-label" id="tenue-t">La tenue</h2>
        </div>

        <div className="wed-block-body">
          <p className="wed-lede" data-reveal>
            {vip
              ? 'Trois journées, trois ambiances. Rien de guindé, juste de quoi être beaux ensemble sur les photos.'
              : 'Deux journées, deux ambiances. Rien de guindé, juste de quoi être beaux ensemble sur les photos.'}
          </p>

          <div className="wed-dress" data-reveal>
            {vip && (
              <article className="wed-dress-panel wed-dress-panel--fri">
                <p className="wed-dress-day">Vendredi soir</p>
                <p className="wed-dress-text">
                  Casual chic, tout simplement.
                </p>
                <p className="wed-dress-note">
                  On se retrouve autour d’une pizza, pas de chichis.
                </p>
              </article>
            )}

            <article className="wed-dress-panel wed-dress-panel--sat">
              <p className="wed-dress-day">Samedi</p>
              <p className="wed-dress-text">
                Costume pour ces messieurs, robe ou combinaison de cérémonie
                pour ces dames.
              </p>
              <p className="wed-dress-note">
                Sortez ce que vous avez de plus beau.
              </p>
            </article>

            <article className="wed-dress-panel wed-dress-panel--sun">
              <p className="wed-dress-day">Dimanche</p>
              <p className="wed-dress-text">
                Place au confort, et un maillot de bain dans le sac.
              </p>
              <p className="wed-dress-note">La piscine vous tend les bras.</p>
            </article>
          </div>
        </div>
      </section>

      <aside className="wed-strip" data-reveal>
        <p className="wed-strip-label">Une seule consigne</p>
        <p className="wed-strip-text">
          Le blanc intégral est réservé aux mariés. Ni robe, ni combinaison,
          ni costume immaculé de tout le week-end.
        </p>
      </aside>

      {photos.length > 0 && (
        <div className="wed-gallery-wrap">
          <section
            className="wed-gallery"
            aria-label="Estelle et Rémi en photos"
            tabIndex={0}
            ref={galleryRef}
            onScroll={updateEdges}
          >
            {photos.map((src) => (
              <img
                key={src}
                className="wed-gallery-img"
                src={src}
                alt=""
                loading="lazy"
                onLoad={updateEdges}
                onError={() =>
                  setPhotos((current) => current.filter((p) => p !== src))
                }
              />
            ))}
          </section>

          <button
            type="button"
            className="wed-gallery-nav wed-gallery-nav--prev"
            onClick={() => scrollGallery(-1)}
            disabled={atStart}
            aria-label="Photos précédentes"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M15 4 L7 12 L15 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            className="wed-gallery-nav wed-gallery-nav--next"
            onClick={() => scrollGallery(1)}
            disabled={atEnd}
            aria-label="Photos suivantes"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M9 4 L17 12 L9 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}

      <section className="wed-block" id="cadeaux" aria-labelledby="cadeaux-t">
        <div className="wed-block-label">
          <h2 className="wed-label" id="cadeaux-t">Et les cadeaux&nbsp;?</h2>
        </div>

        <div className="wed-block-body">
          <p className="wed-quote" data-reveal>
            Le plus beau cadeau, c’est vous : présents, à table, puis sur la
            piste à deux heures du matin.
          </p>
          <p className="wed-lede" data-reveal>
            Nous n’attendons rien d’autre. Ce qui nous tient à cœur, c’est de
            vous recevoir comme il se doit et de faire les choses à notre
            façon, du début à la fin.
          </p>
          <p className="wed-aside" data-reveal>
            Si l’envie d’offrir quelque chose vous tient malgré tout, nous vous
            indiquerons une association qui nous touche particulièrement.
          </p>
        </div>
      </section>

      <section className="wed-block" id="lieu" aria-labelledby="lieu-t">
        <div className="wed-block-label">
          <h2 className="wed-label" id="lieu-t">Le lieu</h2>
        </div>

        <div className="wed-block-body">
          <p className="wed-lede" data-reveal>
            Tout se passe au même endroit, les deux jours.
          </p>
          <div data-reveal>
            <p className="wed-venue">{VENUE.name}</p>
            <p className="wed-address">
              {VENUE.street}
              <br />
              {VENUE.city}
            </p>
            <a
              className="wed-link"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ouvrir dans Google Maps
            </a>
          </div>
        </div>
      </section>

      <section className="wed-end">
        <p className="wed-end-lede">Il ne manque plus que votre réponse.</p>
        <button
          type="button"
          className="wed-end-cta"
          onClick={() => navigate('/rsvp')}
        >
          Répondre à l’invitation
        </button>
        <p className="wed-end-foot">On a hâte. Vraiment.</p>
      </section>
    </main>
  );
};

export default Landing;
